import {
  useEffect,
  useRef,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Button } from "./Button";

const KEYBOARD_RESIZE_STEP = 16;
const LARGE_KEYBOARD_RESIZE_STEP = 64;

interface PaneResizeHandleProps {
  width: number;
  minWidth: number;
  maxWidth: number;
  onResize: (width: number) => void;
  label: string;
  title: string;
  /** Positioning for the hit area; the two panes sit differently in the grid. */
  className: string;
  /** The visible grip inside the hit area. */
  gripClassName: string;
  valueText: string;
  /** Sidebar only: its handle sits inside a clickable pane. */
  stopClickPropagation?: boolean;
}

/**
 * Pointer and keyboard resizing for a vertical pane edge.
 *
 * Pointer capture on the handle rather than the DnD sensors, so a resize drag
 * never starts a calendar drag. Listeners are torn down through a ref so an
 * unmount mid-drag cannot leave them attached to the window.
 */
export function PaneResizeHandle({
  width,
  minWidth,
  maxWidth,
  onResize,
  label,
  title,
  className,
  gripClassName,
  valueText,
  stopClickPropagation = false,
}: PaneResizeHandleProps) {
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => () => cleanupRef.current?.(), []);

  const clamp = (value: number) =>
    Math.min(maxWidth, Math.max(minWidth, Math.round(value)));

  const handlePointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    cleanupRef.current?.();

    const handle = event.currentTarget;
    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startWidth = width;
    handle.setPointerCapture(pointerId);

    const cleanup = () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", cleanup);
      window.removeEventListener("pointercancel", cleanup);
      if (handle.hasPointerCapture(pointerId)) {
        handle.releasePointerCapture(pointerId);
      }
      cleanupRef.current = null;
    };
    const handleMove = (moveEvent: PointerEvent) => {
      moveEvent.preventDefault();
      onResize(clamp(startWidth + moveEvent.clientX - startX));
    };

    cleanupRef.current = cleanup;
    window.addEventListener("pointermove", handleMove, { passive: false });
    window.addEventListener("pointerup", cleanup, { once: true });
    window.addEventListener("pointercancel", cleanup, { once: true });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    event.stopPropagation();
    const direction = event.key === "ArrowLeft" ? -1 : 1;
    const step = event.shiftKey
      ? LARGE_KEYBOARD_RESIZE_STEP
      : KEYBOARD_RESIZE_STEP;
    onResize(clamp(width + direction * step));
  };

  return (
    <Button
      variant="unstyled"
      role="separator"
      aria-label={label}
      aria-orientation="vertical"
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
      aria-valuenow={width}
      aria-valuetext={valueText}
      title={title}
      className={className}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      onClick={
        stopClickPropagation ? (event) => event.stopPropagation() : undefined
      }
    >
      <span className={gripClassName} />
    </Button>
  );
}
